<script lang="ts">
	import { setContext, onMount } from 'svelte';
	import { Contexts } from '#lib/constants/contexts.js';
	import { NewsCategoryTags } from '#lib/websites/news/components/NewsCategoryTags/index.js';
	import { createAuthContext } from '#lib/websites/news/components/NewsHeader/AuthDialogs/utils/createAuthContext/index.js';
	import { NewsHeader } from '#lib/websites/news/components/NewsHeader/index.js';
	import { NewsOverlay } from '#lib/websites/news/components/NewsOverlay/index.js';
	import { NewsSavedTags } from '#lib/websites/news/components/NewsSavedTags/index.js';
	import {
		COGNITO_LAST_AUTH_USER_KEY,
		NEWS_RESTORING_SESSION_ATTR,
	} from '#lib/websites/news/constants/auth.js';
	import {
		ArticleSearchCategory,
		SEARCH_CATEGORIES,
	} from '#lib/websites/news/constants/categories.js';
	import { getSearchVariables } from '#lib/websites/news/pages/NewsHomePage/utils/getSearchVariables/getSearchVariables.js';
	import { isCategoryActive } from '#lib/websites/news/pages/NewsHomePage/utils/isCategoryActive/isCategoryActive.js';
	import { isOAuthResponse } from '#lib/websites/news/utils/isOAuthResponse/isOAuthResponse.js';
	import { loadNewsAuth } from '#lib/websites/news/utils/loadNewsAuth/loadNewsAuth.js';
	import { page } from '$app/state';

	interface Props {
		children?: import('svelte').Snippet;
		forceShowGlobalNav?: boolean;
	}

	let { children, forceShowGlobalNav }: Props = $props();

	let mounted = $state(false);

	onMount(() => {
		// Amplify is otherwise loaded on first use (loadNewsAuth). An OAuth
		// response in the URL is the one case it must handle on arrival, before
		// anything else changes the URL.
		if (isOAuthResponse(window.location.search)) {
			void loadNewsAuth();
		}

		mounted = true;
	});

	let searchParams = $derived(page.url.searchParams);
	let variables = $derived(getSearchVariables(searchParams));
	let path = $derived(page.url.href.split(page.url.origin)[1]);
	let showMenu = $derived(page.data.showMenu ?? true);
	// The app-only pages under /news (terms-mobile) are chrome-free: the app
	// renders its own modal header around them. showMenu only gates the category
	// and saved-tag bars, so without this the NewsHeader still rendered on top
	// of the page inside the app. Mirrors PrivacyLayout's showNavBar guard for
	// /privacy-mobile.
	let showHeader = $derived(page.data.showHeader ?? true);

	let categories = $derived(
		SEARCH_CATEGORIES.map((searchCategory) => ({
			label: searchCategory as ArticleSearchCategory | 'top' | 'all',
			active: isCategoryActive({
				variables,
				searchCategory:
					searchCategory === 'top'
						? ArticleSearchCategory.shoutouts
						: searchCategory,
				path,
			}),
		}))
	);

	setContext(Contexts.Auth, createAuthContext());

	// Pre-paint probe for the saved-tags bar. /news pages are server-rendered,
	// and the server cannot see the Cognito session (it lives in localStorage),
	// so the SSR HTML always contains the default-tags bar. The client-side
	// `isRestoringSession` gate only takes effect at hydration — everything
	// painted before then would flash default tags at a signed-in visitor.
	//
	// This blocking script ships in <head>, runs before the body is painted, and
	// stamps an attribute that CSS in NewsSavedTags uses to hide the bar. The
	// attribute is removed by createAuthContext once the restore settles (user
	// tags loaded, no session after all, or restore failed), at which point the
	// client gate owns visibility. Signed-out visitors have no stored key, so
	// nothing is stamped and their server-rendered bar shows from first paint.
	//
	// The window guard makes re-execution a no-op: the probe must only run on
	// the initial document, never re-stamp after the restore has settled.
	// (`</` must not appear literally, or it would terminate this script block.)
	const sessionProbeScript = `<script>try{if(!window.__newsSessionProbe){window.__newsSessionProbe=1;if(localStorage.getItem('${COGNITO_LAST_AUTH_USER_KEY}')){document.documentElement.setAttribute('${NEWS_RESTORING_SESSION_ATTR}','')}}}catch(e){}<${'/'}script>`;
</script>

<svelte:head>
	<!-- eslint-disable-next-line svelte/no-at-html-tags -- built entirely from constants, no user input -->
	{@html sessionProbeScript}
</svelte:head>

{#if showHeader}
	<NewsHeader {forceShowGlobalNav} {mounted} />
{/if}

{#if showMenu}
	<NewsCategoryTags {categories} {forceShowGlobalNav} />
	<NewsSavedTags {forceShowGlobalNav} />
{/if}

<NewsOverlay />

{@render children?.()}
